import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Users, Eye, EyeOff, ExternalLink, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

interface WorkshopLeader {
  id: string;
  user_id: string;
  business_name: string;
  bio: string | null;
  profile_photo_url: string | null;
  website_url: string | null;
  paid_through: string;
  is_active: boolean;
  created_at: string;
}

interface WorkshopListing {
  id: string;
  slug: string;
  title: string;
  location_name: string;
  price_display: string | null;
  max_participants: number | null;
  workshop_type: string;
  is_active: boolean;
  created_at: string;
  workshop_dates: Array<{ start_date: string; end_date: string }>;
  workshop_photos: Array<{ photo_url: string; sort_order: number }>;
}

interface LeaderWithListings extends WorkshopLeader {
  workshop_listings: WorkshopListing[];
}

const TYPE_LABELS: Record<string, string> = {
  astrophotography: 'Astrophotography',
  deep_sky: 'Deep Sky',
  landscape: 'Landscape',
  nature: 'Nature',
  mixed: 'Mixed',
};

export const WorkshopAdmin: React.FC = () => {
  const [leaders, setLeaders] = useState<LeaderWithListings[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-workshops', {
        body: {},
      });

      if (error) throw error;
      setLeaders(data?.leaders || []);
    } catch (err) {
      console.error('Error loading workshop data:', err);
      toast.error('Failed to load workshop data');
    } finally {
      setLoading(false);
    }
  };

  const toggleListingActive = async (listingId: string, currentActive: boolean) => {
    try {
      const { error } = await supabase.functions.invoke('admin-workshops', {
        body: { action: 'toggle_listing', listingId, isActive: currentActive },
      });
      if (error) throw error;
      toast.success(currentActive ? 'Listing hidden' : 'Listing published');
      loadAll();
    } catch (err) {
      toast.error('Failed to update listing');
    }
  };

  const toggleLeaderActive = async (leaderId: string, currentActive: boolean) => {
    try {
      const { error } = await supabase.functions.invoke('admin-workshops', {
        body: { action: 'toggle_leader', leaderId, isActive: currentActive },
      });
      if (error) throw error;
      toast.success(currentActive ? 'Leader deactivated' : 'Leader activated');
      loadAll();
    } catch (err) {
      toast.error('Failed to update leader');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => (
          <Card key={i} className="animate-pulse rounded-[4px]">
            <CardContent className="h-32" />
          </Card>
        ))}
      </div>
    );
  }

  const totalListings = leaders.reduce((sum, l) => sum + (l.workshop_listings?.length || 0), 0);
  const activeListings = leaders.reduce((sum, l) =>
    sum + (l.workshop_listings?.filter(wl => wl.is_active).length || 0), 0);
  const activeLeaders = leaders.filter(l => l.is_active).length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="rounded-[4px]">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-foreground">{leaders.length}</div>
            <div className="text-xs text-muted-foreground">Workshop Leaders</div>
          </CardContent>
        </Card>
        <Card className="rounded-[4px]">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-foreground">{activeLeaders}</div>
            <div className="text-xs text-muted-foreground">Active Leaders</div>
          </CardContent>
        </Card>
        <Card className="rounded-[4px]">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-foreground">{activeListings}/{totalListings}</div>
            <div className="text-xs text-muted-foreground">Active/Total Listings</div>
          </CardContent>
        </Card>
      </div>

      {/* Leaders + Listings */}
      {leaders.length === 0 ? (
        <Card className="rounded-[4px]">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No workshop leaders yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {leaders.map((leader) => (
            <Card key={leader.id} className={`rounded-[4px] ${!leader.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {leader.profile_photo_url ? (
                      <img src={leader.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-base">{leader.business_name}</CardTitle>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span>Paid through: {leader.paid_through ? format(parseISO(leader.paid_through), 'MMM d, yyyy') : 'N/A'}</span>
                        <span>·</span>
                        <span>{leader.workshop_listings?.length || 0} listing{(leader.workshop_listings?.length || 0) !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={leader.is_active ? 'default' : 'secondary'}>
                      {leader.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => toggleLeaderActive(leader.id, leader.is_active)}>
                      {leader.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {leader.workshop_listings && leader.workshop_listings.length > 0 && (
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {leader.workshop_listings.map((listing) => {
                      const nextDate = listing.workshop_dates
                        ?.filter((d) => d.end_date >= new Date().toISOString().split('T')[0])
                        .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];

                      return (
                        <div key={listing.id} className={`flex items-center justify-between p-3 rounded-[4px] border ${listing.is_active ? 'bg-background' : 'bg-muted/30'}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            {listing.workshop_photos?.[0] && (
                              <img src={listing.workshop_photos[0].photo_url} alt="" className="w-12 h-8 rounded-[2px] object-cover flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-foreground truncate">{listing.title}</span>
                                <Badge variant="outline" className="text-[10px] flex-shrink-0">
                                  {TYPE_LABELS[listing.workshop_type] || listing.workshop_type}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {listing.location_name}</span>
                                {nextDate && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {format(parseISO(nextDate.start_date), 'MMM d')} – {format(parseISO(nextDate.end_date), 'MMM d')}
                                  </span>
                                )}
                                {listing.price_display && <span>{listing.price_display}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <a href={`/workshops/${listing.slug}`} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                            </a>
                            <Button variant="ghost" size="sm" onClick={() => toggleListingActive(listing.id, listing.is_active)}>
                              {listing.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
